import { Spinner } from '@frontend/components/common';
import { DetailActionsFooter, ModalType, useDetailActionsOverContent } from '@frontend/components/mobile';
import { Functionality } from '@frontend/functionality/types';
import { ChevronDown, ChevronUp, ICONS, Package } from '@frontend/icons';
import { CommonMessage, TranslatedString } from '@frontend/lang';
import { PinCodeType } from '@frontend/pin-code/types';
import { SlotBadge } from '@frontend/slot/badge';
import { SpotBadge } from '@frontend/spot/badge';
import { TransactionStateName } from '@frontend/transaction/types';
import { UserBadge } from '@frontend/user/badge';
import { FaCheck, FaXmark } from 'react-icons/fa6';
import { FormattedMessage } from 'react-intl';

import { NavigationHeader } from '../../../header/navigation-header/navigation-header.component';
import ColliStateBadge from '../colli-state-badge/colli-state-badge.component';
import TransactionStateBadge from '../transaction-state-badge/transaction-state-badge.component';
import ConfirmDeleteModal from './confirm-delete-modal/confirm-delete-modal.component';
import useTransactionDetail from './transaction-detail.controller';

const ID = 'transaction-detail';
const TransactionDetail = () => {
    const viewProps = useTransactionDetail();

    const showDelete = viewProps.transaction?.state === TransactionStateName.TRANSACTION_CREATED;
    const showPrint = !!viewProps.transaction && viewProps.canPrint !== false;
    const showMove = !!viewProps.moveInfo && viewProps.transaction?.state === TransactionStateName.TRANSACTION_PICK_UP_PENDING;
    const showContinue = !!viewProps.onContinue;
    const showActions = !viewProps.isLoading && !!viewProps.transaction && !!(showDelete || showPrint || showMove || showContinue);
    const actionsFooter = useDetailActionsOverContent(showActions, viewProps.transaction?.id);

    const actionButtons = [
        {
            label: CommonMessage.BUTTONS.DELETE,
            onClick: () => viewProps.setDeleteModal(true),
            hide: !showDelete
        },
        {
            label: (
                <span className='flex items-center justify-center gap-2'>
                    {viewProps.printing && <Spinner />}
                    {CommonMessage.OBJECTS.PRINTER.PRINT_LABELS}
                </span>
            ),
            onClick: () => viewProps.onPrint(),
            hide: !showPrint
        },
        {
            label: viewProps.moveInfo ? (
                <TranslatedString<Functionality>
                    object={viewProps.moveInfo.functionality}
                    objectKey='name'
                />
            ) : (
                CommonMessage.BUTTONS.MOVE
            ),
            onClick: () =>
                viewProps.navigate(viewProps.moveInfo!.url, {
                    state: { transaction: viewProps.transaction }
                }),
            hide: !showMove
        },
        {
            label: CommonMessage.BUTTONS.CONTINUE,
            onClick: () => viewProps.onContinue!(),
            hide: !showContinue
        }
    ];

    return (
        <div
            id={ID}
            className='relative flex h-dvh w-full flex-col'>
            <NavigationHeader
                title={CommonMessage.OBJECTS.TRANSACTION.DEFAULT}
                onBack={() => viewProps.navigate(-1)}
            />
            <div
                ref={actionsFooter.scrollContainerRef}
                onScroll={showActions ? actionsFooter.onScrollBody : undefined}
                className={`bg-background-default no-scrollbar h-full flex-col overflow-scroll ${showActions ? 'pb-30' : 'pb-20'}`}>
                <div className='flex flex-1 flex-col items-start justify-start gap-3 self-stretch px-5 pt-6'>
                    <div className='flex w-full flex-col items-start justify-start gap-3'>
                        <div className='text-foreground-default justify-start text-base font-medium'>
                            {CommonMessage.DETAIL.DETAILS_OF(CommonMessage.OBJECTS.TRANSACTION.DEFAULT)}
                        </div>
                        {viewProps.isLoading ? (
                            <div
                                aria-busy='true'
                                className='bg-background-surface flex w-full flex-col items-center justify-center gap-3 self-stretch rounded-2xl p-6 text-center'>
                                <Spinner />
                                <div className='text-foreground-default justify-start text-base font-semibold'>
                                    <FormattedMessage
                                        id='mobile.transaction-detail.loading'
                                        defaultMessage='Loading transaction details...'
                                        description='Loading message shown while the transaction detail screen is fetching data'
                                    />
                                </div>
                                <div className='text-brand-muted justify-start text-sm font-medium'>
                                    <FormattedMessage
                                        id='mobile.transaction-detail.loading.description'
                                        defaultMessage='Please wait while we retrieve the latest information.'
                                        description='Secondary loading message shown on the transaction detail screen'
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className='flex w-full flex-col items-start justify-start gap-3'>
                                {!viewProps.transaction ? (
                                    <div className='bg-background-surface text-brand-muted inline-flex w-full items-start justify-start self-stretch rounded-2xl p-4 text-base font-medium'>
                                        <FormattedMessage
                                            id='mobile.transaction-detail.not-found'
                                            defaultMessage='This transaction could not be loaded right now.'
                                            description='Fallback message shown when the transaction detail screen has finished loading without data'
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <div className='bg-background-surface inline-flex w-full flex-col items-start justify-start gap-5 self-stretch rounded-2xl p-4'>
                                            <div className='inline-flex h-6 items-center justify-between self-stretch'>
                                                <div className='text-brand-muted justify-start text-base font-medium'>
                                                    {CommonMessage.OBJECTS.COMMON_FIELDS.STATUS}
                                                </div>
                                                <div className='text-foreground-default justify-start text-base font-medium'>
                                                    <TransactionStateBadge transactionState={viewProps.transaction.state} />
                                                </div>
                                            </div>
                                            <div className='inline-flex h-6 items-center justify-between self-stretch'>
                                                <div className='text-brand-muted justify-start text-base font-medium'>
                                                    {CommonMessage.OBJECTS.TRANSACTION.DESTINATION}
                                                </div>
                                                <div className='text-foreground-default justify-start text-base font-medium'>
                                                    <SpotBadge id={viewProps.transaction.spot_id || undefined} />
                                                </div>
                                            </div>
                                            {viewProps.isReceiver && viewProps.pinCodes.find((p) => p.type === PinCodeType.PICK_UP) && (
                                                <div className='inline-flex h-6 items-center justify-between self-stretch'>
                                                    <div className='text-brand-muted justify-start text-base font-medium'>
                                                        {CommonMessage.BUTTONS.PICK_UP} {CommonMessage.OBJECTS.PIN_CODE.CODE}
                                                    </div>
                                                    <div className='text-foreground-default justify-start text-base font-medium'>
                                                        {viewProps.pinCodes.find((p) => p.type === PinCodeType.PICK_UP)!.code}
                                                    </div>
                                                </div>
                                            )}
                                            {viewProps.isSender && viewProps.pinCodes.find((p) => p.type === PinCodeType.DROP_OFF) && (
                                                <div className='inline-flex h-6 items-center justify-between self-stretch'>
                                                    <div className='text-brand-muted justify-start text-base font-medium'>
                                                        {CommonMessage.BUTTONS.DROP_OFF} {CommonMessage.OBJECTS.PIN_CODE.CODE}
                                                    </div>
                                                    <div className='text-foreground-default justify-start text-base font-medium'>
                                                        {viewProps.pinCodes.find((p) => p.type === PinCodeType.DROP_OFF)!.code}
                                                    </div>
                                                </div>
                                            )}
                                            {viewProps.pickedUpBy && (
                                                <div className='inline-flex h-6 items-center justify-between self-stretch'>
                                                    <div className='text-brand-muted justify-start text-base font-medium'>
                                                        <FormattedMessage
                                                            id='mobile.wizard.pick-up-by.title'
                                                            description='Title for the pick-up by screen'
                                                            defaultMessage='Pick-up by'
                                                        />
                                                    </div>
                                                    <div className='text-foreground-default justify-start text-base font-medium'>
                                                        <UserBadge
                                                            id={viewProps.pickedUpBy.user_id}
                                                            className='ms-0 ps-0'
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                            <div className='inline-flex h-6 items-center justify-between self-stretch'>
                                                <div className='text-brand-muted justify-start text-base font-medium'>
                                                    {CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER}
                                                </div>
                                                <div className='text-foreground-default justify-start text-base font-medium'>
                                                    {viewProps.transaction.tracking_number}
                                                </div>
                                            </div>
                                            <div className='inline-flex h-6 items-center justify-between self-stretch'>
                                                <div className='text-brand-muted justify-start text-base font-medium'>
                                                    {CommonMessage.OBJECTS.TRANSACTION.PURCHASE_ORDER}
                                                </div>
                                                <div className='text-foreground-default justify-start text-base font-medium'>
                                                    {viewProps.transaction.purchase_order}
                                                </div>
                                            </div>
                                            <div className='inline-flex h-6 items-center justify-between self-stretch'>
                                                <div className='text-brand-muted justify-start text-base font-medium'>
                                                    {CommonMessage.OBJECTS.SUPPLIER.DEFAULT}
                                                </div>
                                                <div className='text-foreground-default justify-start text-base font-medium'>
                                                    {viewProps.transaction.supplier ?? '/'}
                                                </div>
                                            </div>
                                        </div>

                                        {viewProps.receivers && (
                                            <div className='bg-background-surface inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-2xl p-4'>
                                                <div className='inline-flex items-center justify-between self-stretch'>
                                                    <div className='flex items-center justify-start gap-4'>
                                                        <div className='outline-border-default relative flex size-12 items-center justify-center rounded-full outline-2'>
                                                            {viewProps.receivers ? (
                                                                <FaCheck className='bg-status-success text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                                                            ) : (
                                                                <FaXmark className='bg-brand-muted text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                                                            )}
                                                            <ICONS.ENTITY.USER className='size-1/2' />
                                                        </div>

                                                        <div className='inline-flex flex-col items-start justify-center gap-1'>
                                                            <div className='text-foreground-default justify-start text-base font-semibold'>
                                                                {CommonMessage.OBJECTS.TRANSACTION.RECEIVER}
                                                            </div>
                                                            {viewProps.receivers.map((receiver) => (
                                                                <div
                                                                    key={receiver.id}
                                                                    className='text-brand-muted justify-start text-base font-medium'>
                                                                    <UserBadge
                                                                        id={receiver.user_id}
                                                                        className='ms-0 ps-0'
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <div className='bg-background-surface inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-2xl p-4'>
                                            <div className='inline-flex items-center justify-between self-stretch'>
                                                <div className='flex items-center justify-start gap-4'>
                                                    <div className='outline-border-default relative flex size-12 items-center justify-center rounded-full outline-2'>
                                                        {viewProps.collis ? (
                                                            <FaCheck className='bg-status-success text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                                                        ) : (
                                                            <FaXmark className='bg-brand-muted text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                                                        )}
                                                        <Package className='size-1/2' />
                                                    </div>

                                                    <div className='inline-flex flex-col items-start justify-center gap-1'>
                                                        <div className='text-foreground-default justify-start text-base font-semibold'>
                                                            {CommonMessage.OBJECTS.PARCEL.PLURAL}
                                                        </div>
                                                        <div className='text-brand-muted justify-start text-base font-medium'>
                                                            {viewProps.collis?.length ?? 0} {CommonMessage.OBJECTS.TRANSACTION.ITEMS}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className='flex flex-col gap-2 self-stretch'>
                                                {viewProps.collis &&
                                                    viewProps.collis.map((colli) => {
                                                        const atOtherLocation = viewProps.isColliAtOtherLocation(colli);
                                                        return (
                                                            <div
                                                                className={`text-foreground-default justify-start self-stretch text-base font-medium${atOtherLocation ? 'opacity-60' : ''}`}
                                                                key={colli.id}>
                                                                <span className='flex flex-row justify-between'>
                                                                    <span className='flex flex-col gap-1'>
                                                                        {CommonMessage.OBJECTS.PARCEL.DEFAULT} ({colli.type})
                                                                        <span className='text-xs text-stone-500'>{colli.label}</span>
                                                                        <ColliStateBadge state={colli.state} />
                                                                    </span>
                                                                    {atOtherLocation && colli.current_spot_id ? (
                                                                        <span className='text-foreground-default inline-flex h-6 items-center justify-start gap-2.5 self-stretch text-sm font-medium'>
                                                                            <SpotBadge
                                                                                id={colli.current_spot_id}
                                                                                className='ms-0 ps-0'
                                                                            />
                                                                        </span>
                                                                    ) : (
                                                                        colli.current_slot_id && (
                                                                            <span className='bg-border-default text-foreground-default inline-flex h-6 items-center justify-start gap-2.5 self-stretch rounded px-1 text-sm font-medium'>
                                                                                {CommonMessage.OBJECTS.SLOT.DEFAULT}:{' '}
                                                                                <SlotBadge
                                                                                    id={colli.current_slot_id || undefined}
                                                                                    className='ms-0 ps-0'
                                                                                />
                                                                            </span>
                                                                        )
                                                                    )}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        </div>

                                        {viewProps.transaction.shipping_notes && (
                                            <>
                                                <div className='text-foreground-default justify-start text-base font-medium'>
                                                    {CommonMessage.OBJECTS.TRANSACTION.SHIPPING_NOTES}
                                                </div>
                                                <div
                                                    data-state='Open'
                                                    className='bg-background-surface inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-2xl p-4'>
                                                    <div className='inline-flex items-center justify-between self-stretch'>
                                                        <div className='flex items-center justify-start gap-4'>{viewProps.transaction.shipping_notes}</div>
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {viewProps.transaction.comments && (
                                            <>
                                                <div
                                                    className='inline-flex items-center justify-between self-stretch'
                                                    onClick={() => viewProps.setCommentsVisible(!viewProps.commentsVisible)}>
                                                    <div className='text-foreground-default justify-start text-center text-base font-medium'>
                                                        {CommonMessage.OBJECTS.TRANSACTION.COMMENTS}
                                                    </div>
                                                    <div className='inline-flex h-5 w-5 flex-col items-center justify-center gap-2.5'>
                                                        {viewProps.commentsVisible ? <ChevronUp /> : <ChevronDown />}
                                                    </div>
                                                </div>
                                                {viewProps.commentsVisible && (
                                                    <div
                                                        data-state='Open'
                                                        className='bg-background-surface inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-2xl p-4'>
                                                        <div className='inline-flex items-center justify-between self-stretch'>
                                                            <div className='flex items-center justify-start gap-4'>{viewProps.transaction.comments}</div>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showActions && (
                <DetailActionsFooter
                    actionsOverContent={actionsFooter.actionsOverContent}
                    buttons={actionButtons}
                />
            )}

            {viewProps.deleteModal === true && (
                <ConfirmDeleteModal
                    handleClose={() => viewProps.setDeleteModal(false)}
                    show={viewProps.deleteModal}
                    type={ModalType.PROMPT}
                    onSubmit={viewProps.onDelete}
                />
            )}
        </div>
    );
};

export default TransactionDetail;
